import React, { useCallback, useEffect, useRef, useState } from 'react';
import JSZip from 'jszip';
import { api } from '../utils/api';
import { toEasternDate } from '../utils/time';
import ConfirmDialog from '../components/ConfirmDialog';
import { PageHeader, Panel, StatusPill, Empty, Skeleton, Icon, usePager, Pager, useToast } from '../../components/ui';

// Facebook export parsers
function extractPostItem(item) {
  const found = (item.data || []).find(d => typeof d.post === 'string' && d.post.trim().length > 10);
  if (!found) return null;
  return {
    content: found.post.trim(),
    date: item.timestamp
      ? new Date(item.timestamp * 1000).toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10),
    id: `fb-${item.timestamp || Math.random().toString(36).slice(2)}`,
  };
}

function parseFBJson(json) {
  let items = json;
  if (!Array.isArray(json)) {
    items = json.your_posts_and_activity || json.posts || json.status_updates || [];
  }
  return Array.isArray(items) ? items.map(extractPostItem).filter(Boolean) : [];
}

function isPostFile(path) {
  const p = path.toLowerCase().replace(/\\/g, '/');
  return (p.includes('/posts/') || p.includes('your_posts') ||
          p.includes('posts_and_reels') || p.includes('status_updates')) && p.endsWith('.json');
}

async function parseFolder(files) {
  const candidates = Array.from(files).filter(f => isPostFile(f.webkitRelativePath || f.name));
  const targets = candidates.length > 0
    ? candidates
    : Array.from(files).filter(f => f.name.endsWith('.json'));
  const posts = [];
  for (const file of targets) {
    try { posts.push(...parseFBJson(JSON.parse(await file.text()))); } catch { /* skip */ }
  }
  return posts;
}

async function parseZip(zipFile) {
  const zip = await JSZip.loadAsync(zipFile);
  const names = Object.keys(zip.files).filter(n => !zip.files[n].dir);
  const targets = names.filter(isPostFile).length > 0 ? names.filter(isPostFile) : names.filter(n => n.endsWith('.json'));
  const posts = [];
  for (const name of targets) {
    try { posts.push(...parseFBJson(JSON.parse(await zip.files[name].async('string')))); } catch { /* skip */ }
  }
  return posts;
}

// Shown while the analysis Lambda runs. Polls for the finished profile and gives up after DURATION seconds.
const DURATION = 65;
const ANALYSIS_STEPS = [
  ['Reading posts', 0], ['Identifying themes', 10], ['Building the voice profile', 30], ['Saving the profile', 55],
];

function AnalysisPanel({ onComplete }) {
  const [elapsed, setElapsed] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const start = Date.now();
    const ticker = setInterval(() => {
      const secs = Math.floor((Date.now() - start) / 1000);
      setElapsed(secs);
      if (secs >= DURATION) clearInterval(ticker);
    }, 500);

    const finish = (profile) => { setDone(true); setTimeout(() => onComplete(profile), 600); };
    const poller = setInterval(async () => {
      try {
        const r = await api.getBrandVoice();
        if (r.profile) { clearInterval(ticker); clearInterval(poller); finish(r.profile); }
      } catch { /* ignore poll errors */ }
    }, 10000);

    const deadline = setTimeout(async () => {
      clearInterval(ticker); clearInterval(poller);
      try {
        const r = await api.getBrandVoice();
        if (r.profile) finish(r.profile);
      } catch { /* ignore */ }
    }, DURATION * 1000);

    return () => { clearInterval(ticker); clearInterval(poller); clearTimeout(deadline); };
  }, [onComplete]);

  const pct = done ? 100 : Math.min(95, Math.round((elapsed / DURATION) * 100));
  const remaining = Math.max(0, DURATION - elapsed);
  const stepIndex = ANALYSIS_STEPS.reduce((idx, [, at], i) => (elapsed >= at ? i : idx), 0);

  return (
    <Panel title={done ? 'Brand voice analysis complete' : 'Brand voice analysis is running'}
      action={done ? <StatusPill status="completed" label="Done" /> : <span className="ops-help">{remaining > 0 ? `about ${remaining}s left` : 'wrapping up'}</span>} flush>
      <p className="ops-help" style={{ padding: '14px 18px 0' }}>Claude is reading your posts and building the profile. This page updates on its own.</p>
      <div className="ops-progress" style={{ maxWidth: 'none', margin: '12px 18px 6px' }}>
        <div className="ops-progress__bar" style={{ width: `${pct}%`, background: done ? 'var(--green)' : undefined }} />
      </div>
      <ul className="ops-list">
        {ANALYSIS_STEPS.map(([label], i) => {
          const status = done || i < stepIndex ? 'completed' : i === stepIndex ? 'running' : null;
          return (
            <li key={label}>
              <div className="ops-list__main"><strong>{label}</strong></div>
              {status ? <StatusPill status={status} label={status === 'completed' ? 'Done' : 'In progress'} /> : <span className="ops-pill ops-pill--neutral">Waiting</span>}
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}

const PLACEHOLDER = `Paste your Facebook posts here. Put --- on its own line between posts.

Example:
Our caregivers go above and beyond every single day. We are so grateful for the dedication they bring to every home. #HomeCare #Michigan

---

Did you know that staying socially connected is one of the best things seniors can do for brain health? HCA provides compassionate companion care to keep your loved one engaged. Call us to learn more! #SeniorCare #AgingInPlace`;

const sectionLabel = { fontSize: 11, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ink-3)', padding: '14px 18px 6px' };

function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
}

// Files the Reel generator needs: the FFmpeg binary, music, background photos, logo and QR code.
function ReelAssetsPanel({ showToast }) {
  const [assets, setAssets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null); // which slot is uploading
  const [pendingDelete, setPendingDelete] = useState(null); // { key, name }

  const load = useCallback(async () => {
    try { setAssets(await api.getReelAssets()); }
    catch { showToast('Could not load the Reel assets', 'error'); }
    finally { setLoading(false); }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const upload = async (type, file) => {
    if (!file) return;
    setUploading(type);
    try {
      const { upload_url } = await api.getReelUploadUrl(type, file.name);
      const ct = type === 'music' ? 'audio/mpeg'
        : type === 'ffmpeg' ? 'application/octet-stream'
          : /\.png$/i.test(file.name) ? 'image/png' : 'image/jpeg';
      const res = await fetch(upload_url, { method: 'PUT', headers: { 'Content-Type': ct }, body: file });
      if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`);
      await load();
      showToast(`${file.name} uploaded`);
    } catch (e) {
      showToast(e.message || 'Upload failed', 'error');
    } finally {
      setUploading(null);
    }
  };

  const confirmDelete = async () => {
    const { key, name } = pendingDelete;
    setPendingDelete(null);
    try {
      await api.deleteReelAsset(key);
      await load();
      showToast(`${name} deleted`);
    } catch (e) {
      showToast(e.message || 'Delete failed', 'error');
    }
  };

  const pickFile = (type, accept) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.onchange = (e) => upload(type, e.target.files[0]);
    input.click();
  };

  const UploadBtn = ({ type, accept, label }) => (
    <button type="button" className="ops-btn ops-btn--sm" onClick={() => pickFile(type, accept)} disabled={uploading === type}>
      <Icon name="upload" /> {uploading === type ? 'Uploading...' : label}
    </button>
  );
  const DeleteBtn = ({ item }) => (
    <button type="button" className="ops-btn ops-btn--danger ops-btn--sm" onClick={() => setPendingDelete(item)}><Icon name="trash" /> Delete</button>
  );

  const ffmpegOk = !!assets?.ffmpeg?.installed;

  return (
    <Panel title="Reel assets" action={<span className="ops-help">Files the agent uses to build animated Reels</span>} flush>
      {loading ? <Skeleton rows={5} /> : (
        <>
          <div style={sectionLabel}>Video engine</div>
          <ul className="ops-list">
            <li>
              <div className="ops-list__main">
                <strong>FFmpeg binary</strong>
                <span>{ffmpegOk ? fmtSize(assets.ffmpeg.size) : 'Reels cannot be generated until it is uploaded'}</span>
                {!ffmpegOk && (
                  <div className="ops-help" style={{ marginTop: 4 }}>
                    Download the static amd64 build from <a className="ops-link" href="https://johnvansickle.com/ffmpeg/" target="_blank" rel="noreferrer">johnvansickle.com/ffmpeg</a> and upload the ffmpeg file.
                  </div>
                )}
              </div>
              <div className="ops-actions">
                <StatusPill status={ffmpegOk ? 'completed' : 'failed'} label={ffmpegOk ? 'Installed' : 'Not installed'} />
                <UploadBtn type="ffmpeg" accept="*" label={ffmpegOk ? 'Replace' : 'Upload'} />
              </div>
            </li>
          </ul>

          <div style={sectionLabel}>Background music</div>
          <p className="ops-help" style={{ padding: '0 18px 8px' }}>
            MP3 tracks played at 25% volume. A random track is picked for each Reel. Free CC0 music: <a className="ops-link" href="https://pixabay.com/music/" target="_blank" rel="noreferrer">Pixabay</a> or <a className="ops-link" href="https://mixkit.co/free-stock-music/" target="_blank" rel="noreferrer">Mixkit</a>.
          </p>
          <ul className="ops-list">
            {assets?.music?.length === 0 && <li><span className="ops-help">No music yet. Reels will be silent until you add a track.</span></li>}
            {assets?.music?.map(t => (
              <li key={t.key}><div className="ops-list__main"><strong>{t.name}</strong><span>{fmtSize(t.size)}</span></div><DeleteBtn item={t} /></li>
            ))}
            <li><UploadBtn type="music" accept=".mp3" label="Add music track" /></li>
          </ul>

          <div style={sectionLabel}>Background photos</div>
          <p className="ops-help" style={{ padding: '0 18px 8px' }}>
            Custom flyer backgrounds, 1080 by 820 px JPG or PNG. When present they replace the AI generated backgrounds.
          </p>
          <ul className="ops-list">
            {assets?.backgrounds?.length === 0 && <li><span className="ops-help">No photos yet. The AI will generate backgrounds on its own.</span></li>}
            {assets?.backgrounds?.map(b => (
              <li key={b.key}><div className="ops-list__main"><strong>{b.name}</strong><span>{fmtSize(b.size)}</span></div><DeleteBtn item={b} /></li>
            ))}
            <li><UploadBtn type="background" accept=".jpg,.jpeg,.png" label="Add background photo" /></li>
          </ul>

          <div style={sectionLabel}>Logo and QR code</div>
          <p className="ops-help" style={{ padding: '0 18px 8px' }}>PNG files used in the flyer footer. Without them the flyer uses a drawn placeholder.</p>
          <ul className="ops-list">
            {[['logo', 'Company logo', assets?.logo], ['qr', 'QR code', assets?.qr]].map(([type, label, data]) => (
              <li key={type}>
                <div className="ops-list__main"><strong>{label}</strong><span>{data?.installed ? fmtSize(data.size) : 'Using placeholder'}</span></div>
                <div className="ops-actions">
                  {data?.installed && <StatusPill status="completed" label="Uploaded" />}
                  <UploadBtn type={type} accept=".png" label={data?.installed ? 'Replace' : 'Upload'} />
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
      {pendingDelete && (
        <ConfirmDialog title="Delete this file?" confirmLabel="Yes, delete" onConfirm={confirmDelete} onCancel={() => setPendingDelete(null)}>
          <p style={{ margin: '0 0 8px', overflowWrap: 'anywhere' }}><strong>{pendingDelete.name}</strong></p>
          <p style={{ margin: 0 }}>Future Reels will no longer use it. This cannot be undone.</p>
        </ConfirmDialog>
      )}
    </Panel>
  );
}

function TagList({ items, tone = 'neutral', large }) {
  if (!items?.length) return <span className="ops-help">None</span>;
  return (
    <div className="ops-actions" style={{ flexWrap: 'wrap' }}>
      {items.map(t => <span key={t} className={`ops-pill ops-pill--${tone}`} style={large ? { fontSize: 16 } : undefined}>{t}</span>)}
    </div>
  );
}

export default function BrandVoicePage() {
  const [showToast, toastNode] = useToast();
  const fileRef = useRef();
  const folderRef = useRef();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pastedText, setPastedText] = useState('');
  const [activeTab, setActiveTab] = useState('paste');
  const [analysisRunning, setAnalysisRunning] = useState(false);
  const [parsedPosts, setParsedPosts] = useState(null);
  const [parseError, setParseError] = useState('');

  const [storedPosts, setStoredPosts] = useState(null); // null until loaded
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [deletingPosts, setDeletingPosts] = useState(false);

  useEffect(() => {
    api.getBrandVoice()
      .then(r => setProfile(r.profile))
      .catch(() => showToast('Could not load the brand voice', 'error'))
      .finally(() => setLoading(false));
  }, [showToast]);

  const loadStoredPosts = useCallback(async () => {
    setLoadingPosts(true);
    try { setStoredPosts(await api.getStoredPosts()); }
    catch { showToast('Could not load the stored posts', 'error'); }
    finally { setLoadingPosts(false); }
  }, [showToast]);

  useEffect(() => {
    if (profile && storedPosts === null) loadStoredPosts();
  }, [profile, storedPosts, loadStoredPosts]);

  const handleAnalysisComplete = useCallback((newProfile) => {
    setProfile(newProfile);
    setAnalysisRunning(false);
    setStoredPosts(null);
    showToast('Brand voice analysis complete');
  }, [showToast]);

  const uploadPostsToS3 = async (posts) => {
    const { upload_url } = await api.getUploadUrl();
    const res = await fetch(upload_url, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(posts) });
    if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`);
    await api.triggerAnalysis();
    showToast(`${posts.length} posts uploaded. Analysis started.`);
    setAnalysisRunning(true);
  };

  const handlePasteSubmit = async () => {
    if (pastedText.trim().length < 50) {
      showToast('Paste at least a few posts before analyzing', 'error');
      return;
    }
    setAnalyzing(true);
    try {
      const res = await api.savePosts({ posts_text: pastedText });
      showToast(`Saved ${res.saved} posts. Analysis started.`);
      setAnalysisRunning(true);
    } catch (e) {
      showToast(e.message || 'Could not save the posts', 'error');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleFilePicked = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setParseError('');
    setParsedPosts(null);
    setUploading(true);
    try {
      let posts = [];
      const first = files[0];
      if (files.length === 1 && first.name.endsWith('.zip')) {
        posts = await parseZip(first);
      } else {
        posts = await parseFolder(files);
        if (posts.length === 0 && files.length === 1 && first.name.endsWith('.json')) {
          try { posts = parseFBJson(JSON.parse(await first.text())); } catch { /* ignore */ }
        }
      }
      if (posts.length === 0) {
        setParseError('No Facebook posts were found. Select the folder that contains your Facebook data export, or a .json file from its posts section.');
        return;
      }
      setParsedPosts(posts);
    } catch (err) {
      setParseError(err.message || 'Could not read the files');
    } finally {
      setUploading(false);
    }
  };

  const resetFileSelection = () => {
    setParsedPosts(null);
    setParseError('');
    if (fileRef.current) fileRef.current.value = '';
    if (folderRef.current) folderRef.current.value = '';
  };

  const handleConfirmUpload = async () => {
    if (!parsedPosts?.length) return;
    setUploading(true);
    try {
      await uploadPostsToS3(parsedPosts);
      setParsedPosts(null);
    } catch (e) {
      showToast(e.message || 'Upload failed', 'error');
    } finally {
      setUploading(false);
      resetFileSelection();
    }
  };

  const handleClearPosts = async () => {
    setConfirmClear(false);
    setDeletingPosts(true);
    try {
      await api.deleteStoredPosts();
      setStoredPosts({ count: 0, posts: [], date_range: null });
      showToast('Stored posts deleted');
    } catch (e) {
      showToast(e.message || 'Delete failed', 'error');
    } finally {
      setDeletingPosts(false);
    }
  };

  const reanalyze = async () => {
    setAnalyzing(true);
    try {
      await api.triggerAnalysis();
      showToast('Re-analysis started. It takes about a minute.');
      setAnalysisRunning(true);
    } catch (e) {
      showToast(e.message || 'Could not start the analysis', 'error');
    } finally {
      setAnalyzing(false);
    }
  };

  const posts = storedPosts?.posts || [];
  const { pageRows, pagerProps } = usePager(posts, [storedPosts], 25);
  const detected = pastedText.split('---').filter(p => p.trim().length > 20).length;

  return (
    <>
      <PageHeader
        title="Brand Voice"
        subtitle="How the agent learned to write like your Page. Built from your past Facebook posts."
        meta={profile ? `Version ${profile.version}, updated ${toEasternDate(profile.updated_at)}, ${profile.sample_size} posts analyzed` : null}
        actions={profile ? (
          <button type="button" className="ops-btn ops-btn--primary" onClick={reanalyze} disabled={analyzing || analysisRunning}>
            <Icon name="refresh" /> {analyzing ? 'Starting...' : 'Re-analyze'}
          </button>
        ) : null}
      />

      {loading ? <Panel><Skeleton rows={5} /></Panel> : (
        <>
          {analysisRunning && <AnalysisPanel onComplete={handleAnalysisComplete} />}

          {!analysisRunning && (
            <Panel flush
              title={(
                <div className="ops-tabs" role="tablist">
                  <button type="button" role="tab" aria-selected={activeTab === 'paste'} className={activeTab === 'paste' ? 'is-active' : ''} onClick={() => setActiveTab('paste')}>Paste posts</button>
                  <button type="button" role="tab" aria-selected={activeTab === 'file'} className={activeTab === 'file' ? 'is-active' : ''} onClick={() => { setActiveTab('file'); resetFileSelection(); }}>Facebook export</button>
                </div>
              )}
              action={<span className="ops-help">{profile ? 'Add more posts to sharpen the profile' : 'Add your posts to build the profile'}</span>}>
              <div style={{ padding: '16px 18px' }}>
                {activeTab === 'paste' && (
                  <>
                    <p className="ops-help" style={{ marginBottom: 10 }}>Copy and paste your Facebook posts below. Put <code>---</code> on its own line between posts.</p>
                    <textarea className="ops-input" style={{ width: '100%', minHeight: 220, lineHeight: 1.6, resize: 'vertical' }} placeholder={PLACEHOLDER}
                      value={pastedText} onChange={e => setPastedText(e.target.value)} aria-label="Pasted posts" />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, gap: 10, flexWrap: 'wrap' }}>
                      <span className="ops-help">{detected} {detected === 1 ? 'post' : 'posts'} detected</span>
                      <button type="button" className="ops-btn ops-btn--primary" onClick={handlePasteSubmit} disabled={analyzing || pastedText.trim().length < 50}>
                        {analyzing ? 'Saving...' : 'Analyze brand voice'}
                      </button>
                    </div>
                  </>
                )}

                {activeTab === 'file' && (
                  <>
                    <div className="ops-note" style={{ marginBottom: 14 }}>
                      <strong>Two ways to add your Facebook data.</strong> Extract the .zip export and choose the folder, or upload the .zip file as is. Only posts are read; messages, photos and other data are ignored.
                    </div>

                    {parsedPosts && (
                      <div className="ops-note" style={{ marginBottom: 14 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                          <div>
                            <strong>Found {parsedPosts.length} posts</strong>
                            <div className="ops-help">
                              {parsedPosts.reduce((a, p) => (p.date < a ? p.date : a), '9999')} to {parsedPosts.reduce((a, p) => (p.date > a ? p.date : a), '0000')}
                            </div>
                          </div>
                          <div className="ops-actions">
                            <button type="button" className="ops-btn ops-btn--sm" onClick={resetFileSelection}>Change file</button>
                            <button type="button" className="ops-btn ops-btn--primary ops-btn--sm" onClick={handleConfirmUpload} disabled={uploading}>
                              {uploading ? 'Uploading...' : `Analyze ${parsedPosts.length} posts`}
                            </button>
                          </div>
                        </div>
                        {parsedPosts.slice(0, 2).map((p, i) => (
                          <p key={i} className="ops-help" style={{ marginTop: 10, lineHeight: 1.5 }}>
                            <span className="mono">{p.date}</span> {p.content.length > 160 ? `${p.content.slice(0, 160)}...` : p.content}
                          </p>
                        ))}
                      </div>
                    )}

                    {parseError && <div className="ops-note ops-note--review" style={{ marginBottom: 14 }}>{parseError}</div>}

                    {!parsedPosts && (
                      <div className="ops-grid-2">
                        <div className={`ops-dropzone${uploading ? ' is-busy' : ''}`} onClick={() => folderRef.current?.click()} role="button" tabIndex={0}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); folderRef.current?.click(); } }}>
                          <strong>{uploading ? 'Scanning...' : 'Select folder'}</strong>
                          Choose the extracted Facebook export folder
                          <input ref={folderRef} type="file" style={{ display: 'none' }} webkitdirectory="true" directory="true" multiple onChange={handleFilePicked} />
                        </div>
                        <div className={`ops-dropzone${uploading ? ' is-busy' : ''}`} onClick={() => fileRef.current?.click()} role="button" tabIndex={0}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileRef.current?.click(); } }}
                          onDragOver={e => e.preventDefault()}
                          onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFilePicked({ target: { files: [f] } }); }}>
                          <strong>{uploading ? 'Scanning...' : 'Upload ZIP or JSON'}</strong>
                          Drop the .zip export or a posts .json file
                          <input ref={fileRef} type="file" accept=".zip,.json" style={{ display: 'none' }} onChange={handleFilePicked} />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </Panel>
          )}

          {profile && (
            <Panel title="Uploaded posts" count={storedPosts?.count} flush
              action={(
                <div className="ops-actions">
                  <span className="ops-help">
                    {storedPosts === null ? 'Loading...'
                      : storedPosts.count === 0 ? 'No posts stored'
                        : storedPosts.date_range ? `${storedPosts.date_range.earliest} to ${storedPosts.date_range.latest}` : null}
                  </span>
                  <button type="button" className="ops-btn ops-btn--sm" onClick={loadStoredPosts} disabled={loadingPosts}><Icon name="refresh" /> Refresh</button>
                  {storedPosts?.count > 0 && (
                    <button type="button" className="ops-btn ops-btn--danger ops-btn--sm" onClick={() => setConfirmClear(true)} disabled={deletingPosts}>
                      <Icon name="trash" /> {deletingPosts ? 'Deleting...' : 'Clear posts'}
                    </button>
                  )}
                </div>
              )}
              foot={posts.length > 0 ? <Pager {...pagerProps} label="posts" /> : null}>
              {storedPosts === null ? <Skeleton rows={3} />
                : storedPosts.count === 0 ? <Empty title="No posts stored">Add your Facebook posts above to rebuild the analysis.</Empty> : (
                  <>
                    <ul className="ops-list">
                      {pageRows.map((post, i) => (
                        <li key={post.id || i}>
                          <div className="ops-list__main" style={{ whiteSpace: 'normal' }}>
                            <span className="mono">{post.date}</span>
                            <div style={{ fontSize: 13.5, lineHeight: 1.55, marginTop: 2 }}>{post.content.length > 240 ? `${post.content.slice(0, 240)}...` : post.content}</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                    {storedPosts.count > posts.length && <p className="ops-help" style={{ padding: '10px 18px' }}>Showing the first {posts.length} of {storedPosts.count} stored posts.</p>}
                  </>
                )}
            </Panel>
          )}

          {!profile && !analysisRunning ? (
            <Panel><Empty title="No brand voice yet">Add your posts above and the agent will build a profile of how your Page writes.</Empty></Panel>
          ) : profile ? (
            <>
              <div className="ops-grid-2">
                <Panel title="Tone and personality"><p style={{ fontSize: 14, lineHeight: 1.7 }}>{profile.tone_description}</p></Panel>
                <Panel title="Core themes"><TagList items={profile.core_themes} /></Panel>
                <Panel title="Hashtags"><TagList items={profile.hashtags} tone="blue" /></Panel>
                <Panel title="Emojis and calls to action">
                  <p className="ops-help" style={{ marginBottom: 8 }}>Emojis</p>
                  <div style={{ marginBottom: 14 }}><TagList items={profile.emojis} large /></div>
                  <p className="ops-help" style={{ marginBottom: 8 }}>Calls to action</p>
                  <TagList items={profile.cta_patterns} />
                </Panel>
                <Panel title="Posting cadence">
                  <p style={{ fontSize: 14, lineHeight: 1.6 }}>{profile.posting_cadence}</p>
                  <p className="ops-help" style={{ marginTop: 10 }}>Average post length: <strong>{profile.avg_post_length} characters</strong></p>
                </Panel>
                <Panel title="Topics to avoid"><TagList items={profile.topics_to_avoid} tone="red" /></Panel>
              </div>
              <Panel title="Sample posts" count={profile.sample_posts?.length}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {(profile.sample_posts || []).map((s, i) => <div key={i} className="ops-note" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{s}</div>)}
                </div>
              </Panel>
            </>
          ) : null}

          <ReelAssetsPanel showToast={showToast} />
        </>
      )}

      {confirmClear && (
        <ConfirmDialog title="Delete all stored posts?" confirmLabel="Yes, delete" onConfirm={handleClearPosts} onCancel={() => setConfirmClear(false)}>
          The current brand voice profile stays in place until you analyze new posts. This cannot be undone.
        </ConfirmDialog>
      )}
      {toastNode}
    </>
  );
}
